import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import { type DragEndEvent, useDndMonitor } from '@dnd-kit/core';
import { useEffect } from 'react';
import { isReservation, type ServiceReservation } from '../apiHelpers';
import { useSeatReservation } from '../hooks/useSeatReservation';
import {
  type TableTimerDictionary,
  useReservationServiceContext,
} from '../state/ReservationServiceContext';
import { useReservationServiceSelectedSeatingContext } from '../state/ReservationServiceSelectedSeatingContext';
import { SeatedFloorPlanTableFactory } from './SeatedFloorPlanTableFactory';

export interface SeatedFloorPlanProps {
  backgroundSrc?: string;
  className?: string;
  highlightedListingIds?: string[];
  tables: HostFloorPlanTable[];
  tableTimersByFloorPlanTableId: TableTimerDictionary;
}

export const ReservationSeatModeFloorPlan = ({
  backgroundSrc,
  className,
  highlightedListingIds = [],
  tableTimersByFloorPlanTableId,
  tables,
}: SeatedFloorPlanProps) => {
  const { id: restaurantId } = useRestaurant();
  const { selectedOccupant } = useReservationServiceContext();
  const { seatReservation, reseatReservation } = useSeatReservation({
    restaurantId,
  });
  const {
    selectedFloorPlanTables,
    setSelectedFloorPlanTables,
    toggleSelectedFloorPlanTable,
  } = useReservationServiceSelectedSeatingContext();
  const occupantIsSeated = Boolean(selectedOccupant?.seatedTableName);

  useEffect(() => {
    if (!occupantIsSeated) {
      const highlightedFloorPlanTablesWithoutASeatedOccupant = tables.filter(
        (table) =>
          table.listings.some((listing) =>
            highlightedListingIds.includes(listing.id),
          ),
      );
      setSelectedFloorPlanTables(
        highlightedFloorPlanTablesWithoutASeatedOccupant,
      );
    }
  }, [occupantIsSeated]);

  const isWithinHighlightedListing = (
    floorPlanTable: HostFloorPlanTable,
  ): boolean => {
    const selectedFloorPlanTableIds = selectedFloorPlanTables.map(
      (table) => table.id,
    );
    return selectedFloorPlanTableIds.includes(floorPlanTable.id);
  };

  useDndMonitor({
    async onDragEnd(event: DragEndEvent) {
      // We don't have access to the selected occupant on drag so we should only perform actions based on the event data.
      const { active, over } = event;

      const floorPlanTable = over?.data.current
        ?.floorPlanTable as HostFloorPlanTable;
      const reservation = active?.data.current?.reservation as
        | ServiceReservation
        | undefined;
      const reservationIsSeated = Boolean(reservation?.seatedTableName);

      if (
        over &&
        active &&
        over.data.current?.accepts.includes(active.data.current?.type) &&
        !!reservation
      ) {
        if (reservationIsSeated) {
          await reseatReservation([floorPlanTable], reservation);
        } else {
          await seatReservation([floorPlanTable], reservation);
        }
      }
    },
  });

  const handleTableOnClick = async (floorPlanTable: HostFloorPlanTable) => {
    if (!isReservation(selectedOccupant)) return;
    toggleSelectedFloorPlanTable(floorPlanTable);
  };

  const floorPlanTablesRenderer: FloorPlanTablesRenderer = (tableIconScale) => (
    <>
      {tables.map((floorPlanTable) => (
        <SeatedFloorPlanTableFactory
          floorPlanTable={floorPlanTable}
          calculateIsHighlighted={isWithinHighlightedListing}
          handleTableOnClick={handleTableOnClick}
          key={floorPlanTable.id}
          timer={tableTimersByFloorPlanTableId[floorPlanTable.id]}
          tableIconScale={tableIconScale}
          enableSeatedTableClick
        />
      ))}
    </>
  );

  return (
    <FloorPlan
      backgroundSrc={backgroundSrc}
      className={className}
      floorPlanTablesRenderer={floorPlanTablesRenderer}
      scaleOnVerticalResize
    />
  );
};
